import { useState } from "react"
import styled from "styled-components"

import { Banner, Text } from "@minuthq/meatball-ui-react"

import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { ParadiseUserCombobox } from "src/components/Paradise/ParadiseUserCombobox"
import { usePutTransferHometokenToUser } from "src/data/homes/queries/homeTokenQueries"
import { THomeToken } from "src/data/homes/types/homeTokenTypes"
import { TParadiseUser } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"
import { SUBSCRIPTION_STATUS, UserRoles } from "src/data/user/user"
import { MDialog } from "src/ui/Dialog/MDialog"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { spacing } from "src/ui/spacing"
import { formatPrice } from "src/utils/formatPrice"
import { formatUtcDate } from "src/utils/l10n"

const FORM_ID = "transfer-form"

export function ParadiseTransferTokensDialog({
  userId,
  tokens,
  open,
  onClose,
}: {
  userId: string
  tokens: THomeToken[]
  open: boolean
  onClose: () => void
}) {
  const [selectedUser, setSelectedUser] = useState<TParadiseUser | null>(null)

  const putTransferHomeTokenToUser = usePutTransferHometokenToUser()

  function performTransfer() {
    if (!selectedUser) {
      return
    }

    const homeTokenIds = tokens
      .map((token) => token.id)
      .filter((i) => i !== undefined)

    if (homeTokenIds.length === 0) {
      return
    }

    putTransferHomeTokenToUser.mutate(
      {
        token_ids: homeTokenIds,
        new_owner_id: selectedUser?.id,
        old_owner_id: userId,
      },
      {
        onSuccess: () => {
          onClose()
        },
      }
    )
  }

  const columns: TableColumn<THomeToken>[] = [
    {
      value: "id",
      label: "id",
      columnWidth: "min-content",
      renderCell: (token) => <Text variant="bodyS">{token.id}</Text>,
    },
    {
      value: "created_at",
      label: "Created at",
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">{formatUtcDate({ date: token.created_at })}</Text>
      ),
    },
    {
      value: "expires_at",
      label: "Expires at",
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">{formatUtcDate({ date: token.expires_at })}</Text>
      ),
    },
    {
      value: "plan_type",
      label: "Plan type",
      columnWidth: "min-content",
      renderCell: (token) => <Text variant="bodyS">{token.plan_type}</Text>,
    },
    {
      value: "amount_paid",
      label: "Amount paid",
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">
          {formatPrice(token.amount_paid / 100, token.currency)}
        </Text>
      ),
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns,
    data: tokens,
  })

  const newOwner = selectedUser

  const hasSubscriptionIssue =
    newOwner?.subscription_id === null ||
    (newOwner?.subscription_status &&
      [SUBSCRIPTION_STATUS.active, SUBSCRIPTION_STATUS.future].includes(
        newOwner.subscription_status as SUBSCRIPTION_STATUS
      ) === false)

  const hasHomeTokenIssue =
    newOwner?.roles.includes(UserRoles.HOMETOKENS) === false

  return (
    <MDialog
      open={open}
      error={
        putTransferHomeTokenToUser.isError
          ? "Failed to transfer tokens"
          : undefined
      }
      confirmButtonProps={{
        disabled: !selectedUser,
        loading: putTransferHomeTokenToUser.isLoading,
      }}
      confirmLabel="Transfer"
      title={`Transfer ${tokens.length} home token${
        tokens.length > 1 ? "s" : ""
      }`}
      onClose={() => {
        onClose()
      }}
      formId={FORM_ID}
      width="large"
    >
      <FormWrapper
        onSubmit={(e) => {
          e.preventDefault()
          performTransfer()
        }}
        id={FORM_ID}
      >
        <ParadiseUserCombobox
          selectedUser={selectedUser}
          onChange={(user) => {
            setSelectedUser(user)
          }}
          required
        />
        {hasSubscriptionIssue && (
          <Banner type="error">
            The new owner must have an active subscription
          </Banner>
        )}
        {hasHomeTokenIssue && (
          <Banner type="error">
            The new owner must be using home tokens (must have the home_tokens
            role)
          </Banner>
        )}
        <Text variant="heading3">Tokens to transfer:</Text>
        <TableWrapper>
          <ParadiseTable
            header={headerElements}
            rows={rows ?? []}
            templateColumns={templateColumns}
          />
        </TableWrapper>
      </FormWrapper>
    </MDialog>
  )
}

const TableWrapper = styled.div`
  max-height: 200px;
  overflow-y: auto;
`

const FormWrapper = styled.form`
  display: grid;
  gap: ${spacing.L};
`
